import { useState } from 'react';
import { track } from '@/lib/analytics';

interface TestimonialsSectionProps {
  onCtaClick?: () => void;
}

const testimonials = [
  {
    text: 'Was früher Tage gedauert hat, ist bei mir jetzt in Minuten fertig. Die Bilder sehen fotorealistisch aus, und dank der Overlays sind meine Exposés direkt gebrandet. Selbst Nachbearbeitungen klappen auf Anhieb.',
    name: 'Sandra Fischer',
    company: 'Accept Immobilien Leipzig',
    logo: '/landing/partners/Logo_Accept_black.svg',
    logoHeight: 48,
  },
  {
    text: 'Schon im Erstgespräch zeige ich Eigentümern, was aus ihrer Immobilie werden kann. Das überzeugt sofort und hat mir Aufträge gebracht, die sonst bei der Konkurrenz gelandet wären.',
    name: 'Sebastian Höppner',
    company: 'Sebastian Höppner Immobilien',
    logo: '/landing/partners/Logo_Sebastian-Hoeppner-Immo.svg',
    logoHeight: 32,
  },
];

const Stars = () => (
  <div className="lp-testi-stars" role="img" aria-label="5 von 5 Sternen">
    {[...Array(5)].map((_, i) => (
      <svg key={i} viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))}
  </div>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ onCtaClick }) => {
  const [current, setCurrent] = useState(0);

  const goTo = (index: number) => {
    const clamped = Math.max(0, Math.min(testimonials.length - 1, index));
    setCurrent(clamped);
    track('Landing Testimonial Navigated', { index: clamped });
  };

  return (
    <section className="lp-testimonials-section">
      <div className="lp-testi-dark">
        <div className="lp-testi-circle lp-testi-circle-left" aria-hidden="true" />
        <div className="lp-testi-circle lp-testi-circle-right" aria-hidden="true" />
        <div className="lp-testi-viewport">
          <div className="lp-testi-track" style={{ transform: `translateX(-${current * 100}%)` }}>
            {testimonials.map((t) => (
              <figure className="lp-testi-slide" key={t.name}>
                <Stars />
                <blockquote className="lp-testi-quote">{t.text}</blockquote>
                <figcaption className="lp-testi-author">
                  <img src={t.logo} alt={t.company} style={{ height: t.logoHeight }} />
                  <span className="lp-testi-divider" aria-hidden="true" />
                  <span className="lp-testi-author-text">
                    <span className="lp-testi-author-name">{t.name}</span>
                    <span className="lp-testi-author-company">{t.company}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
        <div className="lp-testi-arrows">
          <button
            type="button"
            className="lp-testi-arrow lp-testi-arrow-prev"
            onClick={() => goTo(current - 1)}
            disabled={current === 0}
            aria-label="Vorheriges Testimonial"
          >
            <ArrowIcon />
          </button>
          <button
            type="button"
            className="lp-testi-arrow lp-testi-arrow-next"
            onClick={() => goTo(current + 1)}
            disabled={current === testimonials.length - 1}
            aria-label="Nächstes Testimonial"
          >
            <ArrowIcon />
          </button>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="lp-cta-banner">
        <div className="lp-cta-banner-content">
          <h2>Bereit, deine Immobilienfotos zu transformieren?</h2>
          <p className="lp-cta-banner-desc">Teste Estage jetzt kostenlos und erlebe den Unterschied.</p>
          <div className="lp-cta-banner-buttons">
            <button className="lp-btn-primary" onClick={() => { track('Landing CTA Clicked', { location: 'testimonials' }); onCtaClick?.(); }}>
              Jetzt kostenlos testen
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/>
              </svg>
            </button>
          </div>
          <p className="lp-cta-banner-note">
            Registriere dich jetzt und erhalte <strong>20 Credits gratis</strong> mit dem Code <strong>TRYOUT20</strong>.
          </p>
        </div>
        <div className="lp-cta-banner-images">
          <div className="lp-cta-banner-img">
            <img src="/landing/ES_IMG_Web_Kueche1_gruen.webp" alt="Staging Beispiel 1" />
          </div>
          <div className="lp-cta-banner-img">
            <img src="/landing/ES_IMG_Web_Wohnzimmer1_industrial.webp" alt="Staging Beispiel 2" />
          </div>
          <div className="lp-cta-banner-img">
            <img src="/landing/ES_IMG_Web_Schlafzimmer_eingerichtet.webp" alt="Staging Beispiel 3" />
          </div>
        </div>
      </div>
    </section>
  );
};
